import { useState, useEffect, useRef } from 'react';
import CreateProjectModal from './CreateProjectModal';
import AddMembersModal from './AddMembersModal';
import { LayoutGridIcon, ZapIcon, RefreshIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon, PlusIcon, MoreIcon, UserPlusIcon } from './Icons';

const FILTERS = [
  { value: 'all',           label: 'All' },
  { value: 'scrum',         label: 'Scrum',  Icon: LayoutGridIcon },
  { value: 'kanban',        label: 'Kanban', Icon: ZapIcon },
  { value: 'retrospective', label: 'Retro',  Icon: RefreshIcon },
];

const OPEN_KEY   = 'projectDrawerOpen';
const FILTER_KEY = 'projectDrawerFilter';

export default function ProjectDrawer({ projects, onSelectProject, isAdmin, users, onCreateProject, onAddMembers, showToast }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(OPEN_KEY) !== 'false'; } catch { return true; }
  });
  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem(FILTER_KEY) || 'all'; } catch { return 'all'; }
  });
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [menuProject, setMenuProject] = useState(null);
  const [addMembersProject, setAddMembersProject] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, open ? 'true' : 'false'); } catch {}
  }, [open]);

  useEffect(() => {
    try { localStorage.setItem(FILTER_KEY, filter); } catch {}
  }, [filter]);

  useEffect(() => {
    if (!menuProject) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuProject(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuProject]);

  const filtered = projects.filter((p) => {
    const matchType = filter === 'all' || (p.type || 'scrum') === filter;
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const groups = [
    { type: 'scrum',         label: 'Scrum',  Icon: LayoutGridIcon },
    { type: 'kanban',        label: 'Kanban', Icon: ZapIcon },
    { type: 'retrospective', label: 'Retro',  Icon: RefreshIcon },
  ];

  return (
    <>
      <aside className={`project-drawer${open ? '' : ' project-drawer--closed'}`}>
        <button
          className="project-drawer-toggle"
          onClick={() => setOpen((v) => !v)}
          title={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronLeftIcon size={15} /> : <ChevronRightIcon size={15} />}
        </button>

        <div className={`project-drawer-content${open ? '' : ' project-drawer-content--hidden'}`}>
          <div className="project-drawer-heading">
            <span>Projects</span>
            {isAdmin && (
              <button
                className="project-drawer-new-btn"
                onClick={() => setShowCreate(true)}
                title="New project"
              >
                <PlusIcon size={13} />
              </button>
            )}
          </div>

          <div className="project-drawer-search">
            <SearchIcon size={13} className="project-drawer-search-icon" />
            <input
              type="text"
              className="project-drawer-search-input"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="project-drawer-filters">
            {FILTERS.map(({ value, label, Icon }) => (
              <button
                key={value}
                className={`project-drawer-filter${filter === value ? ' active' : ''}`}
                onClick={() => setFilter(value)}
              >
                {Icon && <Icon size={11} />}
                {label}
              </button>
            ))}
          </div>

          <div className="project-drawer-list">
            {filtered.length === 0 ? (
              <p className="project-drawer-empty">No projects found</p>
            ) : (
              groups.flatMap(({ type, label, Icon: GroupIcon }) => {
                const group = filtered.filter((p) => (p.type || 'scrum') === type);
                if (group.length === 0) return [];
                return [
                  <div key={`group-${type}`} className={`project-drawer-group project-drawer-group--${type}`}>
                    <GroupIcon size={11} />
                    <span>{label}</span>
                  </div>,
                  ...group.map((project) => (
                    <div
                      key={project.id}
                      className="project-drawer-item-row"
                    >
                      <button
                        className={`project-drawer-item project-drawer-item--${type}`}
                        onClick={() => onSelectProject(project)}
                      >
                        <span className="project-drawer-item-name">{project.name}</span>
                      </button>
                      {isAdmin && (
                        <div className="project-drawer-item-menu" ref={menuProject?.id === project.id ? menuRef : null}>
                          <button
                            className="project-drawer-item-more"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuProject(menuProject?.id === project.id ? null : project);
                            }}
                            title="More options"
                          >
                            <MoreIcon size={13} />
                          </button>
                          {menuProject?.id === project.id && (
                            <div className="project-drawer-dropdown">
                              <button
                                className="project-drawer-dropdown-item"
                                onClick={() => { setAddMembersProject(project); setMenuProject(null); }}
                              >
                                <UserPlusIcon size={13} /> Add members
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )),
                ];
              })
            )}
          </div>
        </div>
      </aside>

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreate={async (data) => { await onCreateProject(data); setShowCreate(false); }}
          users={users}
        />
      )}

      {addMembersProject && (
        <AddMembersModal
          project={addMembersProject}
          users={users}
          onAdd={onAddMembers}
          onClose={() => setAddMembersProject(null)}
          showToast={showToast}
        />
      )}
    </>
  );
}
